import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Factory } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProcessingLabsList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [labs, setLabs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editLab, setEditLab] = useState(null);
  const [form, setForm] = useState({ name: '', contact: '', address: '', header_crop_pt: '', footer_crop_pt: '' });

  const fetchLabs = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/processing-labs`, { headers: { Authorization: `Bearer ${token}` } });
    setLabs(await res.json());
  }, [token]);

  useEffect(() => { fetchLabs(); }, [fetchLabs]);

  const openCreate = () => { setEditLab(null); setForm({ name: '', contact: '', address: '', header_crop_pt: '', footer_crop_pt: '' }); setShowDialog(true); };
  const openEdit = (l) => { setEditLab(l); setForm({ name: l.name, contact: l.contact || '', address: l.address || '', header_crop_pt: l.header_crop_pt || '', footer_crop_pt: l.footer_crop_pt || '' }); setShowDialog(true); };

  const save = async () => {
    const url = editLab ? `${API}/api/lis/processing-labs/${editLab.id}` : `${API}/api/lis/processing-labs`;
    const method = editLab ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success(editLab ? 'Updated' : 'Created'); setShowDialog(false); fetchLabs(); }
    else toast.error('Failed');
  };

  const deleteLab = async (id) => {
    if (!window.confirm('Delete this processing lab?')) return;
    await fetch(`${API}/api/lis/processing-labs/${id}`, { method: 'DELETE', headers });
    toast.success('Deleted');
    fetchLabs();
  };

  return (
    <div data-testid="lis-processing-labs">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Processing Labs</h1>
          <p className="text-sm text-slate-500">Labs that process your tests</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="add-lab-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Lab</Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {labs.map(l => (
          <Card key={l.id} className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex gap-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Factory className="w-4.5 h-4.5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{l.name}</p>
                    {l.contact && <p className="text-xs text-slate-500 mt-0.5">{l.contact}</p>}
                    {l.address && <p className="text-xs text-slate-400 mt-0.5">{l.address}</p>}
                    {(l.header_crop_pt > 0 || l.footer_crop_pt > 0) && (
                      <p className="text-[10px] text-emerald-600 mt-1" data-testid={`lab-crop-badge-${l.id}`}>Report crop set: {l.header_crop_pt || 0}pt top / {l.footer_crop_pt || 0}pt bottom</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(l)} className="p-1 text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => deleteLab(l.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {labs.length === 0 && (
          <Card className="border-dashed border-slate-300 col-span-full">
            <CardContent className="p-8 text-center text-slate-400">
              <Factory className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No processing labs yet. Add labs to manage B2B pricing.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editLab ? 'Edit Lab' : 'Add Processing Lab'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Lab Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="lab-name-input" />
            <Input placeholder="Contact (phone)" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} data-testid="lab-contact-input" />
            <Input placeholder="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="lab-address-input" />
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-slate-600 mb-1">Report Header/Footer Crop (for this lab's PDF format)</p>
              <p className="text-[10px] text-slate-400 mb-2">
                Set once, based on this lab's report layout: how many points (72pt = 1 inch) of the lab's own header/footer
                to remove before adding your letterhead. Leave 0 if this lab's reports don't need cropping.
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" min="0" placeholder="Header crop (pt)" value={form.header_crop_pt} onChange={e => setForm(f => ({ ...f, header_crop_pt: e.target.value }))} data-testid="lab-header-crop-input" />
                <Input type="number" min="0" placeholder="Footer crop (pt)" value={form.footer_crop_pt} onChange={e => setForm(f => ({ ...f, footer_crop_pt: e.target.value }))} data-testid="lab-footer-crop-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-lab-btn">{editLab ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
